'use client'

import { useRef, useCallback } from 'react'
import { useAccount } from 'wagmi'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Panel } from '@/ui/atoms/panel/Panel'
import SumsubWebSdk from '@sumsub/websdk-react'
import { KYCStatus } from '../types/CardState'
import { CARD_API_ENDPOINT } from '@/config/consts'
import { withTrackingId } from '@/utils/trackedFetch'

interface CardOnboardingProps {
  onNext: (kycStatus?: KYCStatus) => void
}

interface AccessTokenResponse {
  data: {
    token: string
    userId: string
  }
}

interface WebSDKPayload {
  reviewStatus?: string
  reviewResult?: {
    reviewAnswer?: string
  }
  levelName?: string
  attemptCnt?: number
  attemptId?: string
  reviewId?: string
  createDate?: string
  expireDate?: string
  levelAutoCheckMode?: string | null
  priority?: number
}

// Function to fetch access token with only partner ID and API key (no bearer token)
async function fetchSumsubAccessToken(address: string): Promise<AccessTokenResponse> {
  const response = await fetch(
    `${CARD_API_ENDPOINT}/v1/vendor/cards/sumsub/token`,
    withTrackingId(
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ownerAddress: address }),
      },
      address,
    ),
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.data?.error || errorData.error || `Failed to fetch Sumsub access token: ${response.status}`,
    )
  }

  return response.json()
}

const TERMINAL_STATUSES = new Set<KYCStatus>([KYCStatus.APPROVED, KYCStatus.REJECTED])

const SUMSUB_CUSTOMIZATION_NAME = 'HypurrFi'

export function CardOnboarding({ onNext }: CardOnboardingProps) {
  const { address } = useAccount()
  const lastStatusRef = useRef<KYCStatus | null>(null)

  const guardedOnNext = useCallback(
    (status: KYCStatus) => {
      if (lastStatusRef.current && TERMINAL_STATUSES.has(lastStatusRef.current)) {
        return
      }
      lastStatusRef.current = status
      onNext(status)
    },
    [onNext],
  )

  const {
    data: accessTokenData,
    isLoading,
    error,
    refetch,
  } = useQuery<AccessTokenResponse>({
    queryKey: ['sumsub-access-token', address],
    queryFn: () => fetchSumsubAccessToken(address!),
    enabled: !!address, // Only fetch when address is available
    retry: (failureCount: number, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error instanceof Error && error.message.includes('400')) {
        return false
      }
      return failureCount < 2 // Retry up to 2 times for other errors
    },
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  })

  const accessToken = accessTokenData?.data?.token
  const sumsubUserId = accessTokenData?.data?.userId

  return (
    <div className="flex justify-center">
      <Panel.Wrapper className="w-full min-w-[570px] max-w-xl">
        <Panel.Content className="flex flex-col gap-6 p-8">
          <div className="text-center">
            <Typography variant="h3" gradient>
              Onboarding
            </Typography>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex min-h-[400px] items-center justify-center rounded-lg">
              {!address && (
                <div className="text-center">
                  <Typography variant="p" className="text-red-400">
                    Wallet not connected
                  </Typography>
                </div>
              )}

              {address && isLoading && (
                <div className="text-center">
                  <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                  <Typography variant="p" className="text-muted-foreground">
                    Loading KYC verification...
                  </Typography>
                </div>
              )}

              {address && error && (
                <div className="text-center">
                  <Typography variant="p" className="mb-4 text-red-400">
                    Failed to load KYC verification
                  </Typography>
                  <Typography variant="p" className="text-muted-foreground text-sm">
                    {error.message}
                  </Typography>
                  <Button onClick={() => refetch()} className="mt-4" variant="outline">
                    Retry
                  </Button>
                </div>
              )}

              {accessToken && sumsubUserId && !isLoading && !error && (
                <div className="w-full">
                  <SumsubWebSdk
                    accessToken={accessToken}
                    expirationHandler={() => {
                      // Refetch token when it expires
                      refetch()
                    }}
                    config={{
                      lang: 'en',
                      theme: 'dark',
                      userId: sumsubUserId,
                      customizationName: SUMSUB_CUSTOMIZATION_NAME,
                    }}
                    options={{
                      addViewportTag: false,
                      adaptIframeHeight: true,
                    }}
                    onMessage={(type: string, payload: WebSDKPayload) => {
                      if (type === 'idCheck.onApplicantStatusChanged') {
                        if (payload?.reviewStatus === 'completed' && payload?.reviewResult?.reviewAnswer) {
                          const reviewAnswer = payload.reviewResult.reviewAnswer
                          if (reviewAnswer === 'GREEN') {
                            guardedOnNext(KYCStatus.APPROVED)
                          } else if (reviewAnswer === 'RED') {
                            guardedOnNext(KYCStatus.PENDING)
                          } else {
                            guardedOnNext(KYCStatus.PENDING)
                          }
                        } else if (payload?.reviewStatus === 'pending') {
                          const levelName = payload.levelName
                          if (
                            levelName === 'CountryCheck' ||
                            levelName === 'basic-kyc-level' ||
                            levelName === 'basic'
                          ) {
                            guardedOnNext(KYCStatus.REVIEW)
                          } else {
                            guardedOnNext(KYCStatus.PENDING)
                          }
                        } else if (payload?.reviewStatus === 'onHold') {
                          guardedOnNext(KYCStatus.ONHOLD)
                        } else if (payload?.reviewStatus === 'resubmissionRequired') {
                          guardedOnNext(KYCStatus.RESUBMIT)
                        }
                      }

                      if (type === 'idCheck.onApplicantSubmitted') {
                        guardedOnNext(KYCStatus.PENDING)
                      }

                      if (type === 'idCheck.onApplicantResubmitted') {
                        guardedOnNext(KYCStatus.PENDING)
                      }
                    }}
                    onError={(error: any) => {
                      console.error('Sumsub SDK error:', error)
                      const errorCode = typeof error === 'object' ? error?.code : undefined
                      if (errorCode === 'invalid-token') {
                        refetch()
                        return
                      }
                      guardedOnNext(KYCStatus.ONHOLD)
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Panel.Content>
      </Panel.Wrapper>
    </div>
  )
}
